import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FamilyCompanyStatus } from '../../../models/familyCompanyStatus';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-family-company-status-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './family-company-status-list.component.html',
  styleUrl: './family-company-status-list.component.scss'
})
export class FamilyCompanyStatusListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  familyCompanyStatuses = signal<FamilyCompanyStatus[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadFamilyCompanyStatuses();
  }

  loadFamilyCompanyStatuses(): void {
    this.spinnerService.show();
    this.lutService.getFamilyCompanyStatuses().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortFamilyCompanyStatuses(data);
        this.familyCompanyStatuses.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading family company statuses:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortFamilyCompanyStatuses(statuses: FamilyCompanyStatus[]): FamilyCompanyStatus[] {
    return [...statuses].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateFamilyCompanyStatus(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(familyCompanyStatus: FamilyCompanyStatus): void {
  }

  onEditFamilyCompanyStatus(familyCompanyStatus: FamilyCompanyStatus): void {
  }

  onDeleteFamilyCompanyStatus(familyCompanyStatus: FamilyCompanyStatus): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(familyCompanyStatus.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalStatuses = this.familyCompanyStatuses();

          // 1. Optimistically remove from local state immediately
          this.familyCompanyStatuses.update(statuses => statuses.filter(fcs => fcs.id !== familyCompanyStatus.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteFamilyCompanyStatus(familyCompanyStatus.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Family company status deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting family company status:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.familyCompanyStatuses.set(originalStatuses);
              this.toast.error(`<strong>Failed to delete Family Company Status:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: FamilyCompanyStatus, newName: string} | {item: FamilyCompanyStatus, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: FamilyCompanyStatus;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.familyCompanyStatuses.update(statuses => {
      const updated = statuses.map(fcs => fcs.id === event.item.id ? updatedItem : fcs);
      return this.sortFamilyCompanyStatuses(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateFamilyCompanyStatus(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Family company status updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.familyCompanyStatuses.update(statuses => {
          const updated = statuses.map(fcs => fcs.id === event.item.id ? response : fcs);
          return this.sortFamilyCompanyStatuses(updated);
        });
      },
      error: (error) => {
        console.error('Error updating family company status:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.familyCompanyStatuses.update(statuses =>
          statuses.map(fcs => fcs.id === event.item.id ? originalItem : fcs)
        );
        this.toast.error(`<strong>Failed to update Family Company Status:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newStatus: FamilyCompanyStatus = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.familyCompanyStatuses.update(statuses => this.sortFamilyCompanyStatuses([...statuses, newStatus]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createFamilyCompanyStatus({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Family company status created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.familyCompanyStatuses.update(statuses => {
          const updated = statuses.map(fcs => fcs.id === tempId ? response : fcs);
          return this.sortFamilyCompanyStatuses(updated);
        });
      },
      error: (error) => {
        console.error('Error creating family company status:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.familyCompanyStatuses.update(statuses =>
          statuses.filter(fcs => fcs.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Family Company Status:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
